import React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, CalendarClockIcon, CheckIcon, MinusIcon, SearchXIcon } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader';
import { Panel } from '../../components/ui/Panel';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { EmptyState } from '../../components/ui/EmptyState';
import { ProvenanceBadge } from '../../components/trust/ProvenanceBadge';
import { tuitionLabel } from '../../components/discover/CompareTray';
import { myApplications, programmeById, programmes, scholarships, universityProfileById } from '../../data/admissions';

/**
 * One programme, in full — the record a student actually decides on.
 *
 * The deadline and the requirements lead, because those are what force action.
 * Everything else supports them.
 */
export function ProgrammeDetail() {
  const {
    programmeId = ''
  } = useParams();
  const navigate = useNavigate();
  const programme = programmeById(programmeId);
  if (!programme) {
    return <Panel data-id="element-10875" data-mp="2x0gl961krydy">
        <EmptyState icon={<SearchXIcon className="h-5 w-5" data-id="element-10877" data-mp="2wynfru2p7k7j" />} title="Programme not found" description="This programme is not published on Campuzon, or its record has been withdrawn." action={<Button variant="secondary" onClick={() => navigate('/discover')} data-id="element-10878" data-mp="2n4wu0wxyhnvj">
              Back to Discover
            </Button>} data-id="element-10876" data-mp="2x21hr17g9reg" />
      </Panel>;
  }
  const university = universityProfileById(programme.universityId);
  const funding = scholarships.filter(entry => programme.scholarshipIds.includes(entry.id));
  const existing = myApplications.find(entry => entry.programmeId === programme.id);
  const related = programmes.filter(entry => entry.id !== programme.id && entry.field === programme.field);
  return <div className="space-y-6" data-id="element-10879" data-mp="1vcflu4hrbw9c">
      <Link to="/discover" className="inline-flex items-center gap-1.5 text-body-sm font-medium text-ink-muted hover:text-ink" data-id="element-10880" data-mp="1vc6577ilr1gb">
        <ArrowLeftIcon aria-hidden className="h-4 w-4" data-id="element-10881" data-mp="1ppvnhv23vl8o" />
        Discover
      </Link>

      <PageHeader eyebrow={university?.name ?? 'Institution'} title={programme.title} description={`${programme.level} · ${programme.field} · taught in ${programme.language}`} actions={<div className="flex flex-wrap items-center gap-2" data-id="element-10883" data-mp="24e08j5oms8um">
            <Badge plain data-id="element-10884" data-mp="28fu2yw88ggmc">{programme.deliveryMode}</Badge>
            <ProvenanceBadge provenance={programme.provenance} data-id="element-10885" data-mp="28fbyjcbf2w3c" />
          </div>} data-id="element-10882" data-mp="1bpukoa2a63qk" />

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]" data-id="element-10886" data-mp="1unnkv4lirzk9">
        <div className="space-y-5" data-id="element-10887" data-mp="1zmqi42v9d1ho">
          <Panel title="What you need" description="Everything the published record states. Nothing here is inferred." bodyClassName="p-0" data-id="element-10888" data-mp="1zlyupfbtea2h">
            <ul className="divide-y divide-line" data-id="element-10889" data-mp="2s4mdup793f0x">
              {programme.requirements.map(requirement => <li key={requirement} className="flex items-start gap-3 px-5 py-3.5 text-body-sm text-ink sm:px-6" data-id="element-10890" data-mp="31gtlbw20gndq">
                  <MinusIcon aria-hidden className="mt-1 h-4 w-4 shrink-0 text-ink-subtle" data-id="element-10891" data-mp="3b7eev52tv1ps" />
                  {requirement}
                </li>)}
            </ul>
            <div className="border-t border-line px-5 py-4 sm:px-6" data-id="element-10892" data-mp="2ij8u4ibrxcpy">
              <p className="max-w-[74ch] text-body-sm text-ink-muted" data-id="element-10893" data-mp="2illlfm3f8zfo">
                Requirements change between intakes. Confirm against the institution's own admissions
                page before you rely on this — Campuzon publishes what it was given and dates it.
              </p>
            </div>
          </Panel>

          <Panel title="Where it leads" bodyClassName="p-0" data-id="element-10894" data-mp="7rh52x8kvvrz">
            <ul className="divide-y divide-line" data-id="element-10895" data-mp="7ttvlysejegn">
              {programme.careerOutcomes.map(outcome => <li key={outcome} className="flex items-start gap-3 px-5 py-3.5 text-body-sm text-ink sm:px-6" data-id="element-10896" data-mp="2v0fdylqd9cs">
                  <CheckIcon aria-hidden className="mt-1 h-4 w-4 shrink-0 text-ok-700" data-id="element-10897" data-mp="2dgjgrd7wgtul" />
                  {outcome}
                </li>)}
            </ul>
            <div className="border-t border-line px-5 py-4 sm:px-6" data-id="element-10898" data-mp="2mgy40i4fcj0q">
              <Link to="/opportunities" data-id="element-10899" data-mp="2rfskveitzxi7">
                <Button size="sm" variant="secondary" data-id="element-10900" data-mp="2rera7b5q9d10">
                  Roles hiring for this field
                </Button>
              </Link>
            </div>
          </Panel>

          <Panel title="Funding attached to this programme" bodyClassName="p-0" data-id="element-10901" data-mp="3152ewzx8jq8y">
            {funding.length === 0 ? <p className="px-5 py-4 text-body-sm text-ink-muted sm:px-6" data-id="element-10902" data-mp="363ngp3s4yhzi">
                No funding is linked to this programme on Campuzon. Scholarships open to your level may
                still exist — check the Scholarships tab.
              </p> : <ul className="divide-y divide-line" data-id="element-10903" data-mp="13ijf2kaouran">
                {funding.map(entry => <li key={entry.id} className="px-5 py-4 sm:px-6" data-id="element-10904" data-mp="yif8blzl1axm">
                    <Link to={`/discover/scholarships/${entry.id}`} className="text-body font-semibold text-ink hover:underline" data-id="element-10905" data-mp="yl9srdilivr1">
                      {entry.name}
                    </Link>
                    <p className="cz-num mt-0.5 text-body-sm text-ink-muted" data-id="element-10906" data-mp="1m77uwrube6fk">
                      {entry.amountLabel} · {entry.coverage} · {entry.deadlineLabel}
                    </p>
                  </li>)}
              </ul>}
          </Panel>

          {related.length > 0 && <Panel title={`Other ${programme.field} programmes`} description="Same field, different institutions — the comparison most people actually want." bodyClassName="p-0" data-id="element-10907" data-mp="18gl6ptm6jyxq">
              <ul className="divide-y divide-line" data-id="element-10908" data-mp="2k20qhi9a6oki">
                {related.map(entry => <li key={entry.id} className="px-5 py-3.5 sm:px-6" data-id="element-10909" data-mp="2k4d8twiyb0y3">
                    <Link to={`/discover/programmes/${entry.id}`} className="text-body font-medium text-ink hover:underline" data-id="element-10910" data-mp="2p0c0bszksz2q">
                      {entry.title}
                    </Link>
                    <p className="cz-num mt-0.5 text-body-sm text-ink-muted" data-id="element-10911" data-mp="21azsoxjwcz2j">
                      {universityProfileById(entry.universityId)?.shortName} · {tuitionLabel(entry)} ·{' '}
                      {entry.deadlineLabel}
                    </p>
                  </li>)}
              </ul>
            </Panel>}
        </div>

        <div className="space-y-5" data-id="element-10912" data-mp="1weiutbmwb6c9">
          <Panel title="Deadline" data-id="element-10913" data-mp="3moccqli8bhoo">
            <p className="flex items-start gap-2 text-h4 font-semibold text-ink" data-id="element-10914" data-mp="3mq6y20xexbta">
              <CalendarClockIcon aria-hidden className="mt-1 h-5 w-5 shrink-0 text-ink-subtle" data-id="element-10915" data-mp="3qpfrt5sgd9ou" />
              {programme.deadlineLabel}
            </p>
            <p className="mt-2 max-w-[58ch] text-body-sm text-ink-muted" data-id="element-10916" data-mp="1f0rd1zy12nx2">
              Closing date as published. Campuzon does not extend deadlines and cannot submit on your
              behalf.
            </p>
          </Panel>

          <Panel title="Cost and length" data-id="element-10917" data-mp="1k1ouubf4lfy1">
            <dl className="space-y-3" data-id="element-10918" data-mp="1k0vwnyu8fyri">
              <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1" data-id="element-10919" data-mp="1yqurbrmkws28">
                <dt className="text-body-sm text-ink-muted" data-id="element-10920" data-mp="1ts0auwc33fla">Tuition</dt>
                <dd className="cz-num text-body font-medium text-ink" data-id="element-10921" data-mp="1trqgkwk0am9s">{tuitionLabel(programme)}</dd>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1" data-id="element-10922" data-mp="27pf2m7pt7zgf">
                <dt className="text-body-sm text-ink-muted" data-id="element-10923" data-mp="22qvi8bqv6vq6">Duration</dt>
                <dd className="cz-num text-body font-medium text-ink" data-id="element-10924" data-mp="dea39flra96w">
                  {programme.durationMonths} months
                </dd>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1" data-id="element-10925" data-mp="3jp6pzpjfnqlw">
                <dt className="text-body-sm text-ink-muted" data-id="element-10926" data-mp="3npgt8ufuc2x2">Delivery</dt>
                <dd className="text-body font-medium text-ink" data-id="element-10927" data-mp="3nnmc7r7owuy9">{programme.deliveryMode}</dd>
              </div>
            </dl>
            <p className="mt-3 border-t border-line pt-3 text-caption text-ink-subtle" data-id="element-10928" data-mp="1axg112lqts6">
              Tuition as published by the institution. It does not include living costs, and Campuzon
              takes no fee on it.
            </p>
          </Panel>

          <Panel title={existing ? 'Your application' : 'Apply'} data-id="element-10929" data-mp="34t7avuxwoq79">
            {existing ? <>
                <p className="text-body text-ink" data-id="element-10930" data-mp="3epal7o2wvv5a">
                  You have an application in progress at the{' '}
                  <span className="font-medium" data-id="element-10931" data-mp="p0i1bqm8a34m">{existing.stage}</span> stage.
                </p>
                <p className="mt-1.5 text-body-sm text-ink-muted" data-id="element-10932" data-mp="tyteaa7tktr2">
                  {existing.documents.filter(entry => entry.status === 'verified').length} of{' '}
                  {existing.documents.length} documents verified.
                </p>
                <Link to="/discover" className="mt-3 inline-block" data-id="element-10933" data-mp="29isv0d5cnbkk">
                  <Button size="sm" variant="primary" data-id="element-10934" data-mp="24q96wm91cy1i">
                    Open application workspace
                  </Button>
                </Link>
              </> : <>
                <p className="max-w-[58ch] text-body-sm text-ink" data-id="element-10935" data-mp="1vrnuz9y2kh9p">
                  Starting here creates a Campuzon application workspace — a requirements checklist,
                  document ledger and correspondence record. It does not submit anything to the
                  institution.
                </p>
                <Link to="/discover" className="mt-3 inline-block" data-id="element-10936" data-mp="1guwraohd4jea">
                  <Button size="sm" variant="primary" data-id="element-10937" data-mp="3g6ps6w79u8wa">
                    Start an application
                  </Button>
                </Link>
              </>}
          </Panel>

          {university && <Panel title="The institution" data-id="element-10938" data-mp="1wqy1o0suj65c">
              <Link to={`/discover/universities/${university.id}`} className="text-body font-semibold text-ink hover:underline" data-id="element-10939" data-mp="1rt6e1iz8i66y">
                {university.name}
              </Link>
              <p className="mt-0.5 text-body-sm text-ink-muted" data-id="element-10940" data-mp="21r2o07gfqv3h">
                {university.city}, {university.country}
              </p>
            </Panel>}
        </div>
      </div>
    </div>;
}